import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ApiConstants } from '../../../main/constants/ApiConstants';
import axiosInstance from '../../../main/initializers/axios';
import {
  IUMBReservation,
  IUMBReservationItem,
  UMBReservationDayStatus,
  UMBReservationStatus,
} from '../../../main/interfaces/IUMBReservation';

interface Props {
  reservation: IUMBReservation;
  onClose: () => void;
  onChanged: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

const localDate = (date = new Date()) => {
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60000).toISOString().slice(0, 10);
};

const dateKey = (value: string) => value?.slice(0, 10);

const UmbReservationDetailsDialog: React.FC<Props> = ({ reservation, onClose, onChanged, onEdit, onDelete }) => {
  const { t, i18n } = useTranslation();
  const today = localDate();
  const eligible = useMemo(
    () =>
      reservation.items.filter((item) =>
        item.days.some(
          (day) =>
            dateKey(day.serviceDate) === today &&
            day.status === UMBReservationDayStatus.Reserved &&
            day.isActive &&
            !day.umbInventoryId,
        ),
      ),
    [reservation.items, today],
  );
  const [selectedIds, setSelectedIds] = useState<number[]>(eligible.filter((item) => !item.isBusy).map((item) => item.id));
  const [checkingIn, setCheckingIn] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const canEdit = reservation.status === UMBReservationStatus.Confirmed
    && !reservation.items.some((item) =>
      item.days.some((day) => day.status === UMBReservationDayStatus.CheckedIn || day.umbInventoryId),
    );
  const canDelete = !reservation.items.some((item) =>
    item.days.some((day) => day.status === UMBReservationDayStatus.CheckedIn || day.umbInventoryId),
  );

  const toggle = (item: IUMBReservationItem) => {
    if (item.isBusy) return;
    setSelectedIds((current) =>
      current.includes(item.id) ? current.filter((id) => id !== item.id) : [...current, item.id],
    );
  };

  const checkIn = async () => {
    if (!selectedIds.length) {
      toast.error(t('umbReservations.validation.checkInSelection'));
      return;
    }

    setCheckingIn(true);
    try {
      await axiosInstance.post(ApiConstants.umbReservation.checkIn, {
        reservationId: reservation.id,
        serviceDate: today,
        umbReservationItemIds: selectedIds,
      });
      toast.success(t('umbReservations.messages.checkedIn'));
      onChanged();
    } finally {
      setCheckingIn(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm(t('umbReservations.cancelConfirmation'))) return;
    setCancelling(true);
    try {
      await axiosInstance.post(ApiConstants.umbReservation.cancel, {
        reservationId: reservation.id,
        reason: cancelReason.trim() || null,
      });
      toast.success(t('umbReservations.messages.cancelled'));
      onChanged();
    } finally {
      setCancelling(false);
    }
  };

  return createPortal(
    <div className="umbr-modal-overlay" role="dialog" aria-modal="true">
      <div className="umbr-modal umbr-details-modal">
        <div className="umbr-modal-header">
          <div>
            <h2>{t('umbReservations.detailsTitle', { id: reservation.id })}</h2>
            <p>
              {reservation.customerName}
              {reservation.customerPhone ? ` - ${reservation.customerPhone}` : ''}
            </p>
          </div>
          <button type="button" className="umbr-close" onClick={onClose} aria-label={t('generic.close')}>
            x
          </button>
        </div>

        <div className="umbr-modal-body">
          <div className="umbr-detail-summary">
            <Summary label={t('umbReservations.dateRange')} value={`${formatDate(reservation.startDate, i18n.language)} - ${formatDate(reservation.endDate, i18n.language)}`} />
            <Summary label={t('umbReservations.status')} value={t(`umbReservations.statuses.${reservation.status}`)} />
            <Summary label={t('umbReservations.umbrellaCount')} value={String(reservation.umbrellaCount)} />
            <Summary label={t('umbReservations.deposit')} value={reservation.depositAmount == null ? '-' : Number(reservation.depositAmount).toFixed(2)} />
            <Summary
              className="umbr-summary-created"
              label={t('umbReservations.created')}
              value={formatDateTime(reservation.created, i18n.language)}
            />
          </div>

          {reservation.notes && (
            <div className="umbr-notes">
              <strong>{t('umbReservations.notes')}</strong>
              <p>{reservation.notes}</p>
            </div>
          )}

          {eligible.length > 0 && reservation.status === UMBReservationStatus.Confirmed && (
            <section className="umbr-checkin-section">
              <div className="umbr-section-title">
                <div>
                  <h3>{t('umbReservations.todayCheckIn')}</h3>
                  {/* <p>{t('umbReservations.checkInHint')}</p> */}
                </div>
                <button
                  type="button"
                  className="ui-control ui-btn ui-btn--primary"
                  onClick={checkIn}
                  disabled={checkingIn || !selectedIds.length}
                >
                  {checkingIn ? t('umbReservations.checkingIn') : t('umbReservations.checkInCreateTicket')}
                </button>
              </div>
              <div className="umbr-checkin-items">
                {eligible.map((item) => (
                  <label key={item.id} className={`umbr-checkin-item ${item.isBusy ? 'is-busy' : ''}`}>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(item.id)}
                      disabled={item.isBusy}
                      onChange={() => toggle(item)}
                    />
                    <span>
                      <strong>{item.umbName}</strong>
                      <small>
                        {item.umbCode} · {item.areaName || t('umbReservations.noArea')}
                      </small>
                    </span>
                    {item.isBusy && <em>{t('umbReservations.currentlyBusy')}</em>}
                  </label>
                ))}
              </div>
            </section>
          )}

          <section>
            <div className="umbr-section-title">
              <div>
                <h3>{t('umbReservations.dailyAllocation')}</h3>
                {/* <p>{t('umbReservations.dailyAllocationHint')}</p> */}
              </div>
            </div>
            <div className="umbr-allocation-table-wrap">
              <table className="umbr-allocation-table">
                <thead>
                  <tr>
                    <th>{t('umbReservations.umbrella')}</th>
                    <th>{t('area.area')}</th>
                    <th>{t('generic.date')}</th>
                    <th>{t('umbReservations.status')}</th>
                    <th>{t('umbReservations.ticket')}</th>
                  </tr>
                </thead>
                <tbody>
                  {reservation.items.flatMap((item) =>
                    item.days.map((day) => (
                      <tr key={day.id}>
                        <td data-label={t('umbReservations.umbrella')}>
                          <strong>{item.umbName}</strong>
                          <small>{item.umbCode}</small>
                        </td>
                        <td data-label={t('area.area')}>{item.areaName || '-'}</td>
                        <td data-label={t('generic.date')}>{formatDate(day.serviceDate, i18n.language)}</td>
                        <td data-label={t('umbReservations.status')}>
                          <span className={`umbr-day-status is-${day.status}`}>{t(`umbReservations.dayStatuses.${day.status}`)}</span>
                        </td>
                        <td data-label={t('umbReservations.ticket')}>{day.umbInventoryId ?? '-'}</td>
                      </tr>
                    )),
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {reservation.status !== UMBReservationStatus.Cancelled && (
            <section className="umbr-cancel-section">
              <label className="umbr-field">
                <span>{t('umbReservations.cancelReason')}</span>
                <textarea value={cancelReason} onChange={(event) => setCancelReason(event.target.value)} />
              </label>
              <button type="button" className="ui-control ui-btn umbr-danger-btn" onClick={cancel} disabled={cancelling}>
                {cancelling ? t('umbReservations.cancelling') : t('umbReservations.cancelReservation')}
              </button>
            </section>
          )}
        </div>

        <div className={`umbr-modal-footer umbr-details-footer ${canEdit ? 'has-edit' : 'without-edit'}`}>
          {canEdit && (
            <button type="button" className="ui-control ui-btn ui-btn--primary umbr-details-edit-btn" onClick={onEdit}>
              {t('umbReservations.editReservation')}
            </button>
          )}
          <button
            type="button"
            className="ui-control ui-btn umbr-danger-outline-btn umbr-details-delete-btn"
            onClick={onDelete}
            disabled={!canDelete}
          >
            {t('umbReservations.deleteReservation')}
          </button>
          <button type="button" className="ui-control ui-btn ui-btn--secondary umbr-details-close-btn" onClick={onClose}>
            {t('generic.close')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

const Summary: React.FC<{ label: string; value: string; className?: string }> = ({ label, value, className }) => (
  <div className={className}>
    <span>{label}</span>
    <strong>{value}</strong>
  </div>
);

const formatDate = (value: string, locale: string) =>
  value ? new Date(`${dateKey(value)}T00:00:00`).toLocaleDateString(locale) : '-';

const formatDateTime = (value: string, locale: string) =>
  value ? new Date(value).toLocaleString(locale) : '-';

export default UmbReservationDetailsDialog;
